/**
 * Location Delegation Hook
 *
 * BLU-3PL — owner-side "Delegate fulfilment" control. A 3PL org that OWNS a
 * location can point that location's fulfilment at a shared 3PL location it is
 * an active client of. Backend contract (PR #1201):
 *
 *   trpc.locations.setLocationDelegation
 *     input:  { locationId: string; delegatedToLocationId: string | null }
 *             (delegatedToLocationId === null clears the delegation)
 *     output: { locationId: string; delegatedToLocationId: string | null }
 *     errors: TRPCError with user-friendly messages (junction-not-active,
 *             chaining, bound to a partner warehouse, not-found).
 *
 * Mirrors the structure/conventions of useLocationMutations.ts: logs on mutate,
 * invalidates locations.list on success, surfaces toasts via the `settings`
 * i18n namespace, and reports failures other than BAD_REQUEST refusals to Sentry.
 */

import { useLogger } from '@/lib/logger';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from 'react-i18next';

export function useLocationDelegation() {
  const { t } = useTranslation('settings');
  const logger = useLogger();
  const utils = trpc.useUtils();

  const setLocationDelegation = trpc.locations.setLocationDelegation.useMutation({
    onMutate: ({ locationId, delegatedToLocationId }) => {
      logger.info('Setting location delegation', {
        operation: 'set_location_delegation',
        locationId,
        cleared: delegatedToLocationId === null,
      });
    },
    onSuccess: (data) => {
      utils.locations.list.invalidate();

      const cleared = data.delegatedToLocationId === null;

      logger.info('Location delegation updated', {
        operation: 'set_location_delegation',
        locationId: data.locationId,
        cleared,
      });

      toast({
        title: 'Success',
        description: cleared ? t('toast.delegationCleared') : t('toast.delegationSet'),
      });
    },
    onError: (error) => {
      // A BAD_REQUEST is the backend refusing this delegation with a sentence
      // the toast shows as is; a Sentry event for it would only add noise.
      if (error.data?.code === 'BAD_REQUEST') {
        logger.info('Location delegation refused', {
          operation: 'set_location_delegation',
          error: error.message,
        });
      } else {
        Sentry.captureException(error, {
          tags: {
            feature: 'locations',
            operation: 'set_location_delegation',
          },
        });

        logger.error('Failed to set location delegation', {
          operation: 'set_location_delegation',
          error: error.message,
        });
      }

      toast({
        title: 'Error',
        description: error.message || t('toast.failedSetDelegation'),
        variant: 'destructive',
      });
    },
  });

  return {
    setLocationDelegation,
  };
}
